/**
 * Ordering radiologists by surname, then given name — "Dr. Abbott" before
 * "Dr. Baker", and "Dr. Alan Smith" before "Dr. Zoe Smith". Display names are
 * left exactly as they are; only the order changes.
 *
 * Sorting on the display `name` alone doesn't do that. A rad's `name` is
 * "Dr. {Last}", but it becomes "Dr. {First} {Last}" as soon as two rads share a
 * last name, so those two land under their first names — far apart, with
 * unrelated rads between them. Every rad list should sort with the comparators
 * here instead.
 */

/** Titles dropped before a name is split ("Dr. Smith" -> "Smith"). */
const TITLE_RE = /^(?:dr|doctor|prof|professor|mr|mrs|ms|miss)\.?\s+/i;

/** Credentials and generational suffixes, which are never part of the sort key. */
const SUFFIX_RE = /^(?:m\.?d\.?|d\.?o\.?|ph\.?d\.?|r\.?n\.?|jr\.?|sr\.?|i{2,3}|iv|v)$/i;

/**
 * Surname particles. "Juan de Leon" files under "de Leon", not "Leon". Common
 * given names that double as particles (Al, Ben) are deliberately absent — they
 * would swallow the surname of "Al Smith".
 */
const SURNAME_PARTICLES = new Set([
  'van', 'von', 'der', 'den', 'de', 'del', 'della', 'di', 'da', 'dos', 'du',
  'la', 'le', 'los', 'san', 'santa', 'st', 'st.', 'ten', 'ter', 'bin',
]);

/** The shape any rad-ish record shares: a display name, maybe a legal name. */
export type RadNameLike = {
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
};

export type RadNameParts = {
  last: string;
  first: string;
};

/**
 * Base-insensitive compare, so "van Dyke" and "Van Dyke" interleave correctly.
 * Case and accents still break the tie, so names that differ only in those
 * don't collapse to "equal" and drift apart between renders.
 */
function collate(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base' }) || a.localeCompare(b);
}

function tokenize(value: string): string[] {
  return value.split(/\s+/).filter(Boolean);
}

function stripTitles(value: string): string {
  let out = value.trim();
  for (let prev = ''; out !== prev; ) {
    prev = out;
    out = out.replace(TITLE_RE, '').trim();
  }
  return out;
}

/**
 * Drops trailing credentials. One token always survives ("Dr. Rose MD" is a
 * surname, not a credential) unless `allowEmpty`, which callers pass for a
 * fragment that may be nothing but credentials.
 */
function stripSuffixes(tokens: string[], allowEmpty = false): string[] {
  const out = [...tokens];
  const floor = allowEmpty ? 0 : 1;
  while (out.length > floor && SUFFIX_RE.test(out[out.length - 1])) out.pop();
  return out;
}

/** Pulls the surname off the end, particles included. */
function splitOnSurname(tokens: string[]): RadNameParts {
  if (tokens.length === 0) return { last: '', first: '' };
  let surnameStart = tokens.length - 1;
  while (surnameStart > 0 && SURNAME_PARTICLES.has(tokens[surnameStart - 1].toLowerCase())) {
    surnameStart -= 1;
  }
  return {
    last: tokens.slice(surnameStart).join(' '),
    first: tokens.slice(0, surnameStart).join(' '),
  };
}

/**
 * Best-effort split of a display name into surname and given name. Handles
 * "Dr. Smith", "Dr. Alan Smith", "Smith, Alan", "Smith, Alan MD" and
 * "Alan Smith, MD"; the
 * trailing "*" the intake folders use as a marker is ignored.
 */
export function parseRadName(name: string | null | undefined): RadNameParts {
  const cleaned = stripTitles((name ?? '').replace(/\*/g, ' '));
  if (cleaned === '') return { last: '', first: '' };

  const comma = cleaned.indexOf(',');
  if (comma !== -1) {
    const beforeComma = stripSuffixes(tokenize(cleaned.slice(0, comma)));
    // Further commas just separate credentials ("Alan Smith, MD, PhD"); left
    // attached they'd hide "MD," from the suffix list.
    const afterComma = stripSuffixes(tokenize(cleaned.slice(comma + 1).replace(/,/g, ' ')), true);
    // A comma that only fences off credentials ("Alan Smith, MD") is not the
    // "Last, First" form: the left side is a whole name, surname last.
    if (afterComma.length === 0) return splitOnSurname(beforeComma);
    // "Last, First" is already in sort order — take the halves as written.
    return { last: beforeComma.join(' '), first: afterComma.join(' ') };
  }

  return splitOnSurname(stripSuffixes(tokenize(cleaned)));
}

/** A rad's surname and given name, from the legal fields when they're set. */
export function radNameParts(rad: RadNameLike): RadNameParts {
  const last = (rad.last_name ?? '').trim();
  if (last !== '') return { last, first: (rad.first_name ?? '').trim() };
  return parseRadName(rad.name);
}

/**
 * "Smith, Alan" — surname first and no "Dr.", for lists sorted by
 * `compareRadiologists` so the label reads in the order the rows are in. Just
 * "Smith" when no given name is known. The intake "*" marker is dropped.
 */
export function radListLabel(rad: RadNameLike): string {
  const { last, first } = radNameParts(rad);
  const surname = last.replace(/\*/g, '').trim();
  const given = first.replace(/\*/g, '').trim();
  if (surname === '') return (rad.name ?? '').trim();
  return given === '' ? surname : `${surname}, ${given}`;
}

/** Comparator for rad records: surname, then given name, then display name. */
export function compareRadiologists(a: RadNameLike, b: RadNameLike): number {
  const left = radNameParts(a);
  const right = radNameParts(b);
  return (
    collate(left.last, right.last) ||
    collate(left.first, right.first) ||
    collate((a.name ?? '').trim(), (b.name ?? '').trim())
  );
}

/**
 * Comparator for places that only carry the rendered name — shifts, vacations,
 * forecast rows. Falls back to the whole string so ties stay deterministic.
 */
export function compareRadNames(a: string | null | undefined, b: string | null | undefined): number {
  const left = parseRadName(a);
  const right = parseRadName(b);
  return (
    collate(left.last, right.last) ||
    collate(left.first, right.first) ||
    collate((a ?? '').trim(), (b ?? '').trim())
  );
}
